<template>
  <PageChrome>
    <template #title>About Us</template>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="card">
      <div class="lead">
        This information is included as initial context for agents (e.g. Prospecting) when <code>about_us</code> is enabled.
      </div>

      <div class="field">
        <label>Company description</label>
        <textarea class="textarea" rows="4" v-model="form.company" placeholder="Who are we?" />
      </div>

      <div class="field">
        <label>What we sell</label>
        <textarea class="textarea" rows="8" v-model="form.product" placeholder="What do we sell and how do we help?" />
      </div>

      <div class="actions">
        <button class="btn" :disabled="saving" @click="reload">Reset</button>
        <button class="btn primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>

      <div v-if="success" class="success">{{ success }}</div>
    </div>
  </PageChrome>
</template>

<script setup>
import { onMounted, onBeforeUnmount, reactive, ref } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import { apiFetch } from '../../services/api.ts'
import { useChatScope } from '../composables/useChatScope'

const { setScope, clearScope } = useChatScope()

const error = ref('')
const success = ref('')
const saving = ref(false)

const form = reactive({
  company: '',
  product: '',
})

function normalizeAboutUs(obj) {
  const o = obj && typeof obj === 'object' ? obj : {}
  return {
    company: typeof o.company === 'string' ? o.company : '',
    product: typeof o.product === 'string' ? o.product : '',
  }
}

async function reload() {
  error.value = ''
  success.value = ''
  try {
    const resp = await apiFetch('/settings/about')
    const a = normalizeAboutUs(resp?.about_us)
    form.company = a.company
    form.product = a.product
  } catch (e) {
    error.value = e?.message || 'Failed'
  }
}

async function save() {
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await apiFetch('/settings/about', {
      method: 'PUT',
      body: {
        about_us: {
          company: form.company || '',
          product: form.product || '',
        },
      },
    })
    success.value = 'Saved'
  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  reload()
})

onBeforeUnmount(() => {
  clearScope()
})
</script>

<style scoped>
.card {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 12px;
  padding: 14px;
  max-width: 900px;
}

.lead {
  color: var(--muted);
  font-size: 13px;
  margin-bottom: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

label {
  font-size: 12px;
  font-weight: 700;
  color: var(--text);
}

.textarea {
  width: 100%;
  border: 1px solid var(--border);
  background: var(--field-bg);
  color: var(--text);
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  resize: vertical;
}

.actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 6px;
}

.error {
  margin-bottom: 10px;
  color: var(--danger);
}

.success {
  margin-top: 10px;
  color: var(--success);
}

</style>
